import { atom } from "jotai";

import type { AlertFragmentFragment } from "../../__generated__/gql/graphql";
import { graphql } from "../../graphql";
import type { GraphQLClient } from "@package/graphql";

/**
 * Statuses:
 * - **Paused**: The alert is paused and will not trigger
 * - **Active**: The alert is active and will trigger
 */
export type TAlert = Omit<AlertFragmentFragment, "status"> & {
  status: "Paused" | "Active";
};

export type TAlertOptions = Omit<TAlert, "column_id" | "page_id" | "product_id" | "row_id" | "user_id" | "id">;

export const triggeredAlertsQueueAtom = atom<(TAlert & { initial: boolean })[]>([]);

export const AlertFragment = graphql(`
	fragment AlertFragment on alerts {
		id
		limit
		_id
		column_id
		desktop_notification_sent
		field_selector
		note
		page_id
		product_id
		push_notification_sent
		recurring
		row_id
		sound
		status
		triggered_at
		user_id
		value_below_limit
		created_at
		expired_at
	}
`);

// Expired alerts are intentionally included: Manage Alerts shows them in a
// read-only "Expired" state (sorted last) so the user can review or delete them.
// Expiry filtering happens client-side against the current date.
export const AlertQuery = graphql(`
  query AlertsQuery($userId: String!) {
    alerts(where: { user_id: { _eq: $userId } }) {
      ...AlertFragment
    }
  }
`);

export const AlertQueryByPageId = graphql(`
	query AlertsQueryByPageId($userId: String!, $pageId: String!) {
		alerts(where: { user_id: { _eq: $userId }, page_id: { _eq: $pageId } }) {
			...AlertFragment
		}
	}
`);

export const AlertQueryByPageIds = graphql(`
	query AlertsQueryByPageIds($pageIds: [String!]!) {
		alerts(where: { page_id: { _in: $pageIds } }) {
			...AlertFragment
		}
	}
`);

export const AlertExistsQueryById = graphql(`
	query AlertExistsQueryById($id: String!) {
		alerts_by_pk(id: $id) {
			id
			status
			triggered_at
			desktop_notification_sent
			push_notification_sent
		}
	}
`);

export const AlertQueryById = graphql(`
	query AlertQueryById($id: String!) {
		alerts_by_pk(id: $id) {
			...AlertFragment
		}
	}	
`);

export const AlertsInsertMany = graphql(`
	mutation AlertsInsertMany($objects: [alerts_insert_input!]!) {
		insert_alerts(objects: $objects) {
			affected_rows
		}
	}
`);

export const AlertsUpdate = graphql(`
	mutation AlertsUpdate($id: String!, $values: alerts_set_input) {
		update_alerts_by_pk(pk_columns: { id: $id }, _set: $values) {
			id
		}
	}
`);

export const AlertDeleteMutation = graphql(`
	mutation AlertDeleteMutation($id: String!) {
		delete_alerts_by_pk(id: $id) {
			id
		}
	}
`);

export const UserPushSubscriptions = graphql(`
	query UserPushSubscriptions($userId: String!) {
		push_subscriptions(where: { user_id: { _eq: $userId } }) {
			user_id
			subscriptions
		}
	}
`);

export const UserPushSubscriptionsUpsert = graphql(`
	mutation UserPushSubscriptionsUpsert(
		$userId: String!
		$subscriptions: jsonb!
	) {
		insert_push_subscriptions_one(
			object: { user_id: $userId, subscriptions: $subscriptions }
			on_conflict: {
				constraint: push_subscriptions_pkey
				update_columns: [subscriptions]
			}
		) {
			user_id
		    subscriptions
		}
	}
`);

export async function alertExists(hasura: GraphQLClient, id: string) {
  return await hasura.execute(AlertExistsQueryById, { id }).then((res) => res?.data?.alerts_by_pk);
}
